import { GRID_GLSL, HASH } from './common';

/** Scatter: one point per bird into a field atlas, additive blending. */
export const SCATTER_VS = /* glsl */ `#version 300 es
precision highp float;
precision highp int;
precision highp sampler2D;
uniform sampler2D uPos;
uniform sampler2D uVel;
uniform ivec2 uSimSize;
uniform vec3 uBoxMin;
uniform vec3 uBoxSize;
uniform vec3 uAnchor;
uniform vec3 uG;
uniform float uTX;
uniform vec2 uAtlas;
uniform int uCoarse;
uniform float uScale;
out vec4 vOut0;
out vec4 vOut1;
void main() {
  int id = gl_VertexID;
  ivec2 tc = ivec2(id % uSimSize.x, id / uSimSize.x);
  vec4 P = texelFetch(uPos, tc, 0);
  vec4 V = texelFetch(uVel, tc, 0);
  vec3 g = clamp(floor((P.xyz - uBoxMin) / uBoxSize * uG), vec3(0.0), uG - 1.0);
  vec2 tile = vec2(mod(g.z, uTX), floor(g.z / uTX));
  vec2 texel = tile * uG.xy + g.xy + 0.5;
  gl_Position = vec4(texel / uAtlas * 2.0 - 1.0, 0.0, 1.0);
  gl_PointSize = 1.0;
  if (uCoarse == 1) {
    vOut0 = vec4(1.0, (P.xyz - uAnchor) / (uBoxSize * 0.5)) * uScale;
    vOut1 = vec4(0.0);
  } else if (uCoarse == 2) {
    // Whole flock in one texel: count + summed velocity, and summed relative position.
    vOut0 = vec4(1.0, V.xyz) * uScale;
    vOut1 = vec4((P.xyz - uAnchor) / (uBoxSize * 0.5), 0.0) * uScale;
  } else {
    // V.w is agitation with the escape side in its sign: sum both the magnitude
    // (how agitated the neighbourhood is) and the signed value (which way it zigs).
    vOut0 = vec4(1.0, V.xyz) * uScale;
    vOut1 = vec4(abs(V.w), V.w, 0.0, 0.0) * uScale;
  }
}
`;

export const SCATTER_FS2 = /* glsl */ `#version 300 es
precision highp float;
in vec4 vOut0;
in vec4 vOut1;
layout(location = 0) out vec4 o0;
layout(location = 1) out vec4 o1;
void main() { o0 = vOut0; o1 = vOut1; }
`;

export const SCATTER_FS1 = /* glsl */ `#version 300 es
precision highp float;
in vec4 vOut0;
in vec4 vOut1;
layout(location = 0) out vec4 o0;
void main() { o0 = vOut0; }
`;

/**
 * Update: one fragment per bird. Reads the previous state and the fields,
 * writes the next state. uMode 0 initialises from the seed instead.
 *
 * Per-bird state:
 *   pos.w   wing phase
 *   vel.w   agitation (0..1) with the escape side in its sign
 *   aux     roll, size variance, glide, wave state
 *   wave state >= 0: excitation accumulator; fires at 1.
 *              <  0: -state in (0, 1] is the manoeuvre phase (0.4 s),
 *                    (1, 1 + REF] is refractory (1.8 s); then back to 0.
 */
export const UPDATE_FS = /* glsl */ `#version 300 es
precision highp float;
precision highp int;
precision highp sampler2D;
${HASH}
${GRID_GLSL}
uniform sampler2D uPos;
uniform sampler2D uVel;
uniform sampler2D uAux;
uniform sampler2D uFine;
uniform sampler2D uAgit;
uniform sampler2D uCoarse;
uniform sampler2D uGlobal;
uniform ivec2 uSimSize;
uniform int uMode;
uniform float uDt;
uniform float uTime;
uniform uint uSeed;
uniform vec3 uAnchor;
uniform vec3 uBoxMin;
uniform vec3 uBoxSize;
uniform vec4 uB1;          // cohesion, separation, alignment, cruise speed
uniform vec4 uB2;          // nervousness, turn rate, flap rate, crowding
uniform float uSpread;     // roost radius, m
uniform vec4 uPredator;    // xyz, force radius (0 = none)
uniform vec3 uPredatorVel; // m/s; seeding needs > 5 m/s
uniform vec4 uStartle;     // xyz, radius of a seeded point startle (0 = none)
uniform float uWaveRate;   // excitation per second beside a fully agitated neighbourhood
uniform float uAlarm;      // 0..1, whole-flock alarm after an attack
uniform vec3 uAlarmFrom;   // where the last attack came from
layout(location = 0) out vec4 oPos;
layout(location = 1) out vec4 oVel;
layout(location = 2) out vec4 oAux;

const float TAU = 6.28318530718;
const float PI = 3.14159265359;
const float MAN_T = 0.4;     // manoeuvre length, s
const float REF_RATIO = 4.5; // refractory length in manoeuvre units (1.8 s)

void main() {
  ivec2 tc = ivec2(gl_FragCoord.xy);
  uint id = uint(tc.y * uSimSize.x + tc.x);

  if (uMode == 0) {
    float cruise0 = uB1.w;
    vec3 r = hash3((id * 3u + 11u) ^ uSeed);
    vec3 r2 = hash3((id * 3u + 977u) ^ (uSeed * 7u));
    vec3 dirR = normalize(r * 2.0 - 1.0 + vec3(1e-4, 2e-4, 3e-4));
    float rad = pow(r2.x, 1.0 / 3.0) * uSpread * 0.8;
    vec3 pos = uAnchor + dirR * rad * vec3(1.0, 0.4, 1.0);
    pos.y = max(pos.y, 3.0);
    float heading = hash1(uSeed ^ 0xABCDu) * TAU;
    vec3 v0 = vec3(cos(heading), 0.0, sin(heading)) * cruise0 + (r2 - 0.5) * cruise0 * 0.5;
    oPos = vec4(pos, r2.y * TAU);
    oVel = vec4(v0, 0.0);
    oAux = vec4(0.0, r2.z, 0.0, 0.0);
    return;
  }

  vec4 P = texelFetch(uPos, tc, 0);
  vec4 V = texelFetch(uVel, tc, 0);
  vec4 A = texelFetch(uAux, tc, 0);
  vec3 p = P.xyz;
  vec3 v = V.xyz;
  float agit = abs(V.w);
  float side = V.w < 0.0 ? -1.0 : 1.0;
  float phase = P.w;
  float roll = A.x;
  float sizeVar = A.y;
  float glide = A.z;
  float st = A.w;
  float dt = uDt;
  float alarm = uAlarm;

  float cohesion = uB1.x * (1.0 + 0.6 * alarm);
  float separation = uB1.y;
  float alignment = uB1.z;
  float cruise = uB1.w * (1.0 + 0.15 * alarm);
  float nerv = uB2.x;
  float turnRate = uB2.y;
  float flapRate = uB2.z * (1.0 + 0.2 * alarm);
  float crowding = uB2.w;

  float speed = max(length(v), 0.01);
  vec3 dir = v / speed;
  vec3 sideVec = cross(dir, vec3(0.0, 1.0, 0.0));
  float sl = length(sideVec);
  sideVec = sl > 1e-3 ? sideVec / sl : vec3(1.0, 0.0, 0.0);

  // Local neighbourhood from the fine field.
  vec3 cell = uBoxSize / FG;
  vec4 f = sampleAtlas(uFine, p, uBoxMin, uBoxSize, FG, FTX, FATLAS) * FINE_SCALE;
  float n = f.x;
  bool hasN = n > 0.05;
  vec3 vmean = hasN ? f.yzw / n : v;
  vec4 fa = sampleAtlas(uAgit, p, uBoxMin, uBoxSize, FG, FTX, FATLAS) * FINE_SCALE;
  float agitMean = hasN ? fa.x / n : 0.0;
  float sideMean = fa.y;
  vec3 grad = vec3(
    sampleAtlas(uFine, p + vec3(cell.x, 0.0, 0.0), uBoxMin, uBoxSize, FG, FTX, FATLAS).x
      - sampleAtlas(uFine, p - vec3(cell.x, 0.0, 0.0), uBoxMin, uBoxSize, FG, FTX, FATLAS).x,
    sampleAtlas(uFine, p + vec3(0.0, cell.y, 0.0), uBoxMin, uBoxSize, FG, FTX, FATLAS).x
      - sampleAtlas(uFine, p - vec3(0.0, cell.y, 0.0), uBoxMin, uBoxSize, FG, FTX, FATLAS).x,
    sampleAtlas(uFine, p + vec3(0.0, 0.0, cell.z), uBoxMin, uBoxSize, FG, FTX, FATLAS).x
      - sampleAtlas(uFine, p - vec3(0.0, 0.0, cell.z), uBoxMin, uBoxSize, FG, FTX, FATLAS).x
  ) * FINE_SCALE / (2.0 * cell);

  // Centre of mass of the wider neighbourhood from the coarse field.
  vec4 c = sampleAtlas(uCoarse, p, uBoxMin, uBoxSize, CG, CTX, CATLAS) * COARSE_SCALE;
  vec3 com = c.x > 0.05 ? uAnchor + (c.yzw / c.x) * (uBoxSize * 0.5) : uAnchor;

  // Whole-flock mean velocity, and a slow seeded wander of the preferred heading.
  vec4 gsum = texelFetch(uGlobal, ivec2(0), 0) * GLOBAL_SCALE;
  vec3 vGlobal = gsum.x > 0.5 ? gsum.yzw / gsum.x : v;
  float wanderPhase = hash1(uSeed ^ 0x5151u) * TAU + uTime * 0.22 + 0.8 * sin(uTime * 0.09 + hash1(uSeed ^ 0x77u) * TAU);
  vec3 wander = vec3(cos(wanderPhase), 0.0, sin(wanderPhase));

  vec3 acc = vec3(0.0);

  // Alignment: match the local mean velocity. Agitated birds react faster.
  acc += alignment * (1.5 + 3.0 * agit) * (vmean - v);
  // Weak pull toward the flock's common heading, which stops milling, plus the wander.
  vec3 gDir = length(vGlobal) > 0.5 ? normalize(vGlobal) : dir;
  acc += alignment * 0.6 * (gDir * cruise - v);
  acc += wander * 1.2;

  // Cohesion: toward the local centre of mass, saturating.
  vec3 toCom = com - p;
  float dCom = length(toCom);
  acc += cohesion * 3.0 * toCom / (dCom + 10.0);

  // Separation: down the log-density gradient, only where crowded. An alarmed
  // flock balls up and tolerates a higher density.
  float n0 = mix(15.0, 400.0, crowding * crowding) * (1.0 + 0.6 * alarm);
  vec3 sep = -grad / (n + 1.0) * 40.0 * smoothstep(0.0, n0, n);
  float sepMag = length(sep);
  if (sepMag > 15.0) sep *= 15.0 / sepMag;
  acc += separation * sep;

  // Jostling inside an over-dense core, so it diffuses instead of collapsing.
  vec3 jit = hash3((id * 5u + uint(uTime * 60.0) * 3u) ^ uSeed) - 0.5;
  acc += jit * (1.0 * (0.5 + 3.0 * nerv) + 6.0 * max(n / n0 - 1.0, 0.0));

  // Roost: a soft pull that only bites beyond the roost radius.
  vec3 dA = p - uAnchor;
  dA.y *= 2.0;
  float rA = length(dA);
  vec3 toA = -dA / max(rA, 1.0);
  acc += toA * (0.6 * smoothstep(0.0, uSpread, rA) + 7.0 * smoothstep(uSpread, uSpread * 1.8, rA));

  // Alarm: the whole flock turns away from where the falcon came from and flies
  // lower. Weak enough that the roost pull wins within a few seconds.
  if (alarm > 0.2) {
    vec3 away = com - uAlarmFrom;
    float al = length(away);
    if (al > 1.0) acc += (alarm - 0.2) * 0.4 * (away / al * cruise - v);
  }

  // Keep altitude: real flocks are much wider than tall.
  acc.y += -0.12 * (p.y - uAnchor.y * (1.0 - 0.12 * alarm));

  // Ground avoidance.
  acc.y += 14.0 * smoothstep(30.0, 0.0, p.y);

  bool manoeuvring = st < 0.0 && -st <= 1.0;
  bool refractory = st < -1.0;
  bool fire = false;
  float fireSide = side;

  // Predator: a capsule ahead of the falcon. Birds flee sideways to its line
  // and those still ahead of it react most; birds well inside fire a manoeuvre.
  if (uPredator.w > 0.0) {
    vec3 pv = uPredatorVel;
    float pvl = length(pv);
    vec3 pdir = pvl > 0.5 ? pv / pvl : vec3(0.0);
    vec3 seg = pv * 0.6;
    float tseg = pvl > 0.5 ? clamp(dot(p - uPredator.xyz, seg) / max(dot(seg, seg), 1e-4), 0.0, 1.0) : 0.0;
    vec3 e = p - (uPredator.xyz + seg * tseg);
    float r = length(e);
    float R = uPredator.w;
    if (r < R) {
      float s = 1.0 - r / R;
      vec3 en = e / max(r, 0.3);
      vec3 ePerp = e - dot(e, pdir) * pdir;
      float epl = length(ePerp);
      vec3 ePerpN = epl > 0.3 ? ePerp / epl : sideVec;
      vec3 push = normalize(0.7 * ePerpN + 0.3 * en);
      float ahead = 0.5 + 0.5 * dot(en, pdir);
      acc += push * 45.0 * s * s * ahead;
      if (s > 0.3 && pvl > 5.0 && !manoeuvring && !refractory) {
        fire = true;
        fireSide = dot(sideVec, ePerpN) < 0.0 ? -1.0 : 1.0;
      }
    }
  }

  // A seeded point startle: a few birds fire for no visible reason.
  if (uStartle.w > 0.0) {
    float r = length(p - uStartle.xyz);
    if (r < uStartle.w && !manoeuvring && !refractory) {
      fire = true;
      fireSide = hash1((id * 17u) ^ uSeed ^ 0x51u) < 0.5 ? -1.0 : 1.0;
    }
  }

  // Copying: excitation builds while neighbours manoeuvre and leaks away with a
  // ~2 s memory, so a bird does not stay primed at the threshold forever after a
  // wave has passed; fire past 1 and take the neighbours' side.
  if (st >= 0.0) {
    st *= exp(-dt / 2.0);
    st += dt * uWaveRate * max(agitMean - 0.08, 0.0);
    if (st > 1.0) {
      fire = true;
      fireSide = sideMean < 0.0 ? -1.0 : 1.0;
    }
  }
  if (fire) {
    st = -1e-4;
    side = fireSide;
    manoeuvring = true;
  }
  float mphase = manoeuvring ? clamp(-st, 0.0, 1.0) : 0.0;

  // The manoeuvre itself: a coherent sideways zig with a roll, faster wings.
  float speedMul = 1.0;
  float flapMul = 1.0;
  float turnMul = 1.0;
  float rollBias = 0.0;
  if (manoeuvring) {
    float w = sin(PI * mphase);
    acc += side * sideVec * 14.0 * w;
    rollBias = side * 1.2 * w;
    speedMul = 1.2;
    flapMul = 1.3;
    turnMul = 1.5;
    agit = 1.0;
  } else {
    agit *= exp(-dt / 1.3);
  }
  if (st < 0.0) {
    st -= dt / MAN_T;
    if (st < -(1.0 + REF_RATIO)) st = 0.0;
  }

  // Integrate with limits on acceleration, turn rate and speed.
  float accMag = length(acc);
  if (accMag > 35.0) acc *= 35.0 / accMag;
  vec3 vNew = v + acc * dt;
  float sNew = length(vNew);
  vec3 dNew = sNew > 0.01 ? vNew / sNew : dir;
  float maxTurn = turnRate * (1.0 + 1.5 * agit) * turnMul * dt;
  float ang = acos(clamp(dot(dir, dNew), -1.0, 1.0));
  if (ang > maxTurn) dNew = normalize(mix(dir, dNew, maxTurn / ang));
  float sTarget = cruise * speedMul * (1.0 + 0.3 * agit) * (0.9 + 0.2 * sizeVar);
  float s = mix(sNew, sTarget, 1.0 - exp(-dt * 1.5));
  s = clamp(s, cruise * 0.5, cruise * 1.6);
  vec3 vOut = dNew * s;
  vec3 pOut = p + vOut * dt;
  pOut.y = max(pOut.y, 1.0);

  // Bank angle from lateral acceleration plus the manoeuvre roll, smoothed.
  vec3 side2 = cross(dNew, vec3(0.0, 1.0, 0.0));
  float sl2 = length(side2);
  side2 = sl2 > 1e-3 ? side2 / sl2 : vec3(1.0, 0.0, 0.0);
  float rollTarget = atan(dot(acc, side2), 9.81) + rollBias;
  roll = mix(roll, rollTarget, 1.0 - exp(-dt * 5.0));

  // Intermittent gliding; none when agitated or alarmed.
  float gSeed = hash1((id * 7u + uint(floor(uTime * 0.7 + float(id % 97u) * 0.1))) ^ (uSeed * 11u));
  float glideTarget = gSeed < (0.35 - 0.3 * agit) * (1.0 - alarm) ? 1.0 : 0.0;
  glide = mix(glide, glideTarget, 1.0 - exp(-dt * 4.0));

  phase += dt * flapRate * flapMul * (0.9 + 0.2 * sizeVar) * (1.0 + 0.3 * agit) * TAU;
  phase = mod(phase, TAU);

  oPos = vec4(pOut, phase);
  oVel = vec4(vOut, agit * side);
  oAux = vec4(roll, sizeVar, glide, st);
}
`;
